import { defineStepper } from '@stepperize/react';
import { useTranslation } from 'react-i18next';
import { redirect, useLoaderData } from 'react-router';

import { logout } from '@cold-ambulance/ui';

import { auth, getIdentity } from '../../firebase.tsx';
import SignupAccount from './signup-account.tsx';
import SignupContact from './signup-contact';
import SignupDocuments from './signup-documents.tsx';
import SignupSignatory from './signup-signatory.tsx';

const Stepper = defineStepper(
  { id: 'step-1', title: 'البيانات التفصيلية' },
  { id: 'step-2', title: 'البيانات التفصيلية' },
  { id: 'step-3', title: 'البيانات التفصيلية' },
  { id: 'step-4', title: 'جهة الشخص المفوض بالتوقيع' },
);

export type Step = (typeof Stepper.steps)[number];

export function StepCircles({ step, index }: { step: Step; index: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-none grow-0 flex-row items-center justify-end gap-3 self-stretch rounded-[12px] bg-[#e9f6ed] px-6 py-2">
      <div className="text-sm font-semibold text-black">
        {t('signup.stepTitle')}
      </div>
      <div className="ms-auto flex items-center gap-2">
        <FractionCircle
          numerator={index + 1}
          denominator={Stepper.steps.length}
        />
      </div>
    </div>
  );
}

function FractionCircle({
  numerator,
  denominator,
}: {
  numerator: number;
  denominator: number;
}) {
  const label = `${numerator}/${denominator}`;
  const progress = numerator / denominator;
  const circumference = 2 * Math.PI * 14; // radius is 14 (32px / 2 - 2px for stroke)
  const strokeDasharray = `${circumference * progress} ${circumference}`;

  return (
    <div
      className="relative flex size-8 items-center justify-center"
      aria-label={`step ${label}`}
      role="img"
    >
      {/* Background circle */}
      <svg className="absolute size-8 -rotate-90" viewBox="0 0 32 32">
        <circle
          cx="16"
          cy="16"
          r="14"
          fill="none"
          stroke="#D9D9D9"
          strokeWidth="2"
        />
      </svg>

      {/* Progress circle */}
      <svg className="absolute size-8 -rotate-90" viewBox="0 0 32 32">
        <circle
          cx="16"
          cy="16"
          r="14"
          fill="none"
          stroke="#22C55E"
          strokeWidth="2"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
        />
      </svg>

      {/* Label */}
      <span className="relative z-10 text-[10px] font-semibold text-gray-700">
        {label}
      </span>
    </div>
  );
}

export function Signup() {
  const data = useLoaderData() as Awaited<ReturnType<typeof signupLoader>>;
  const methods = Stepper.useStepper({
    initialStep: data.step,
  });
  return (
    <Stepper.Scoped>
      {methods.switch({
        'step-1': (step) => (
          <SignupAccount
            index={0}
            step={step}
            onSubmitSuccess={() => methods.next()}
          />
        ),
        'step-2': (step) => (
          <SignupContact
            index={1}
            step={step}
            onSubmitSuccess={() => methods.next()}
            onSignOut={async () => {
              logout(auth).then(() => {
                window.location.href = '/';
              });
            }}
            newUser={data.newUser ?? true}
          />
        ),
        'step-3': (step) => (
          <SignupDocuments
            index={2}
            step={step}
            onBack={() => methods.prev()}
            onSubmitSuccess={() => methods.next()}
            newUser={data.newUser ?? true}
          />
        ),
        'step-4': (step) => (
          <SignupSignatory
            index={3}
            step={step}
            onBack={() => methods.prev()}
          />
        ),
      })}
    </Stepper.Scoped>
  );
}

export async function signupLoader() {
  const identity = await getIdentity();
  if (!identity) {
    return { step: 'step-1' } as const;
  }
  if (!identity.contactId?.trim()) {
    return { step: 'step-2', newUser: false } as const;
  }
  if (!identity.companyDocumentsId?.trim()) {
    return { step: 'step-3', newUser: false } as const;
  }
  if (!identity.signatoryEmail?.trim()) {
    return { step: 'step-4', newUser: false } as const;
  }
  throw redirect('/');
}
